// src/components/contracts/ContractActions.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEdit,
  faTrash,
  faComments,
  faFileUpload,
  faBuildingColumns,
  faChevronDown,
  faChevronUp,
  faTasks,
  faPlusCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { EntityType, UploadContexts, MODULES } from '../../constants/globalConstants';
import { ContractDeposit } from './ContractApi';
import { TaskAssignmentModal } from '../task-assignment/TaskAssignmentModal';

// ✅ Permission utilities (same helpers used in Project/General actions)
import {
  PermissionAction,
  applyPermissionsToActions,
  readUserFromLocalStorage,
} from '../../utils/permissions';

interface ContractActionsProps {
  contract: ContractDeposit;
  onDelete: (id: string) => void;
}

type ActionItem = {
  icon: any;
  label: string;
  className: string;
  onClick: () => void;
  disabled?: boolean;
  // if omitted, the defaultModuleName passed to applyPermissionsToActions will be used
  moduleName?: string;
  // allow single or multiple required permissions; any one suffices
  required: PermissionAction | PermissionAction[] ;
};

const ContractActions: React.FC<ContractActionsProps> = ({ contract, onDelete }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [isTaskModalOpen, setTaskModalOpen] = useState(false);

  const user = readUserFromLocalStorage();

  const openTaskModal = () => setTaskModalOpen(true);
  const closeTaskModal = () => setTaskModalOpen(false);

  const navigateToGeneralTaskCreate = () => {
    navigate('/general-tasks/create', {
      state: {
        isPrepopulated: true,
        readOnlyFields: true,
        entityType: EntityType.CONTRACT,
        entityId: contract.id,
        taskType: 'CONTRACT_TASK',
        metadata: {
          contractName: contract.contractName,
          contractType: contract.contractType,
          referenceNo: contract.referenceNo,
          licenseHolderName: contract.licenseHolderName || '',
          status: contract.status,
        },
        title: 'Create General Task for Contract',
        description: 'This task is being created in the context of a contract.',
      },
    });
  };

  // Build the full action list first (with module + required perms)
  let actionButtons: ActionItem[] = [
    {
      icon: faEye,
      label: 'View',
      className: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => navigate(`/contracts/${contract.id}`),
      moduleName: MODULES.CONTRACTS, // Contracts
      required: 'view',
    },
    {
      icon: faEdit,
      label: 'Edit',
      className: 'bg-yellow-500 hover:bg-yellow-600',
      onClick: () => navigate(`/contracts/${contract.id}/edit`),
      moduleName: MODULES.CONTRACTS,
      required: 'edit',
    },
    {
      icon: faTrash,
      label: 'Delete',
      className: 'bg-red-600 hover:bg-red-700',
      onClick: () => onDelete(contract.id),
      moduleName: MODULES.CONTRACTS,
      required: 'delete',
    },
    {
      icon: faBuildingColumns,
      label: 'Add Security Deposit',
      className: 'bg-indigo-500 hover:bg-indigo-600',
      onClick: () => navigate(`/contracts/${contract.id}/security-deposit`),
      // If you have a dedicated module for deposits, replace with MODULES.PAYMENTS or MODULES.CONTRACT_DEPOSITS
      moduleName: MODULES.CONTRACTS,
      required: ['edit', 'create'], // either canEdit or canCreate is fine
    },
    {
      icon: faFileUpload,
      label: 'Add Supporting Docs',
      className: 'bg-teal-600 hover:bg-teal-700',
      onClick: () =>
        navigate('/uploads', {
          state: {
            context: UploadContexts.CONTRACT_SUPPORT_DOCS,
            entityId: contract.id,
            entityType: EntityType.CONTRACT,
            metadata: {
              contractName: contract.contractName,
              contractType: contract.contractType,
              referenceNo: contract.referenceNo,
              licenseHolderName: contract.licenseHolderName || '',
              status: contract.status,
            },
            title: 'Upload Supporting Documents for Contract',
            description: 'Upload any supporting documents related to this contract.',
            uploadType: 'CONTRACT_SUPPORT_DOCS',
            maxFiles: 10,
            allowedFileTypes: ['application/pdf', 'image/*', 'application/msword', 'video/*'],
          },
        }),
      moduleName: MODULES.UPLOADS, // Uploads/Documents module
      required: ['view', 'create', 'edit'], // any one is enough to allow entry
    },
    {
      icon: faComments,
      label: 'Add Comments',
      className: 'bg-blue-500 hover:bg-blue-600',
      onClick: () =>
        navigate('/comments', {
          state: {
            context: UploadContexts.CONTRACT_SUPPORT_DOCS,
            entityId: contract.id,
            entityType: EntityType.CONTRACT,
            metadata: {
              contractName: contract.contractName,
              contractType: contract.contractType,
              referenceNo: contract.referenceNo,
              licenseHolderName: contract.licenseHolderName || '',
              status: contract.status,
            },
            title: 'Comments for Contract',
            description: 'Contract Comments',
            uploadType: 'CONTRACT_SUPPORT_DOCS',
            maxFiles: 10,
            allowedFileTypes: ['application/pdf', 'image/*', 'application/msword', 'video/*'],
          },
        }),
      moduleName: MODULES.COMMENTS, // Comments module
      required: ['view', 'create', 'edit'], // permit if they can comment OR create/edit
    },
    {
      icon: faTasks,
      label: 'Assign Task',
      className: 'bg-purple-600 hover:bg-purple-700',
      onClick: openTaskModal,
      moduleName: MODULES.TASK_ASSIGNMENTS, // or MODULES.TASKS if you split it
      required: ['create', 'edit'], // either assign or edit permission
    },
    {
      icon: faPlusCircle,
      label: 'Add Task',
      className: 'bg-green-600 hover:bg-green-700',
      onClick: navigateToGeneralTaskCreate,
      moduleName: MODULES.GENERAL_TASKS, // creating a general task in the context of a contract
      required: 'create',
    },
  ];

  // Apply permissions. Default module for items that didn't specify moduleName:
  actionButtons = applyPermissionsToActions(
    actionButtons,
    MODULES.CONTRACTS, // default module
    'hide',            // strategy: "hide" disallowed actions. Use "disable" to keep them visible but inactive.
    user
  );

  return (
    <>
      <div className="space-y-1">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 flex items-center justify-center gap-2 text-xs font-medium"
          title={expanded ? 'Hide Actions' : 'Show Actions'}
        >
          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
          {expanded ? 'Hide Actions' : 'Show Actions'}
        </button>

        {expanded ? (
          <div className="space-y-1 transition-all duration-300 ease-in-out">
            {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
              <button
                key={label}
                className={`${className} text-white px-2 py-1 rounded w-full flex items-center justify-center gap-2 disabled:opacity-50`}
                onClick={onClick}
                disabled={!!disabled}
                title={label}
              >
                <FontAwesomeIcon icon={icon} />
                {label}
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1 mt-1 transition-all duration-300 ease-in-out">
            {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
              <button
                key={label}
                className={`${className} text-white p-2 rounded flex items-center justify-center disabled:opacity-50`}
                onClick={onClick}
                disabled={!!disabled}
                title={label}
              >
                <FontAwesomeIcon icon={icon} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Task Assignment Modal */}
      {isTaskModalOpen && (
        <TaskAssignmentModal
          isOpen={isTaskModalOpen}
          onClose={closeTaskModal}
          entityId={contract.id}
          entityType={EntityType.CONTRACT}
          taskType="CONTRACT_TASK"
          task={null}
        />
      )}
    </>
  );
};

export default ContractActions;